import {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {formatRelativeTime} from '../utils/calendar';
import {binarySearch} from "../utils/search";
import {
    selectComment,
    selectCommentList,
    selectCommentThread,
    selectContent,
    selectContentList,
    selectFeedEntry,
    selectFeedIsLoaded,
    selectPost,
    selectPosts,
    selectProfile,
    selectThread,
    selectUsername
} from "./selectors";
import {normalizeCommunityName} from "./communities";

export const useLoggedIn = () => {
    return useSelector((state) => Boolean(selectUsername(state)));
};

export const useBaseCommunity = () => {
    return useSelector((state) => state.baseCommunity);
};

export const useFeedAdUnits = (key) => {
    return useSelector((state) => {
        const selectedFeedItem = state.feedAdUnits[key];
        return selectedFeedItem ? selectedFeedItem : null;
    });
};

export const useFeedState = (key) => {
    return useSelector((state) => {
        const selectedFeedItem = state.feedState.find(item => item.key === key);
        return selectedFeedItem ? selectedFeedItem.data : null;
    });
};

export const useIsFeedLoaded = (key) => {
    return useSelector(useCallback(selectFeedIsLoaded(key), [key]));
};

export const useGoogleAdsEnabled = () => {
    return useSelector((state) => state.googleAdsEnabled);
};

export const useSaveClicked = () => {
    return useSelector((state) => state.saveClicked);
};

export const useStoreProfile = () => {
    return useSelector(selectProfile);
};

export const useSelectedCommunity = () => {
    return useSelector((state) => state.selectedCommunity);
};

export const useStoreFeed = (key, withHidden=false) => {
    const {ids, more, loading, loaded, timestamp, polled} = useSelector(selectFeedEntry(key)) || {};
    const posts = useSelector(selectPosts(ids || [], withHidden));
    return {posts, more: more && !loading, loading, loaded, timestamp, polled, cleared: !ids};
};

export const useStoreComment = (id) => {
    return useSelector(selectComment(id));
};

export const useStoreCommentList = (key) => {
    return useSelector(selectCommentList(key));
};

export const useStoreCommentThread = (key) => {
    return useSelector(useCallback(selectCommentThread(key), [key]));
};

export const useStorePostComments = (id, commentSort) => {
    return useStoreCommentList(`p:${id}:${commentSort}`);
};

export const useStoreChildComments = (id) => {
    return useStoreCommentList(`c/${id}`);
};

export const useStoreThread = (key) => {
    return useSelector(selectThread(key));
};

export const useStorePost = (id, commentSort=null) => {
    return useSelector(selectPost(id, commentSort));
};

export const useStoreContentList = (key) => {
    const {items, more, loading, loaded, timestamp, polled} = useSelector(selectContentList(key)) || {};
    const content = useSelector(selectContent(items || []));
    return { content, more: more && !loading, loading: !items || loading, loaded, timestamp, polled };
};

export const useStoreCommunity = (name) => {
    const community = useSelector(({communityMap}) => communityMap.get(normalizeCommunityName(name)));
    const moderates = useSelector((state) => selectProfile(state).moderates || []);
    const subscribed = useSelector((state) => selectProfile(state).subscribed || []);
    return useMemo(() => {
        switch (normalizeCommunityName(name)) {
            case 'win':
                return {title: "Trending"};
            case 'home':
                return {title: "Home"};
            default:
                return community ? {
                    ...community,
                    is_moderator: moderates.includes(community?.name),
                    is_subscribed: subscribed.includes(community?.name),
                } : {title: name};
        }
    }, [name, community, moderates, subscribed]);
};

export const useUsername = () => useSelector(selectUsername);

export const useRelativeTime = (t, full) => {
    const referenceTime = useSelector(({ time }) => time);
    return useMemo(() => formatRelativeTime(t, referenceTime, full), [t, referenceTime, full]);
};

export const useRelativeTimeFormatter = () => {
    const referenceTime = useSelector(({ time }) => time);
    /* eslint-disable react-hooks/exhaustive-deps */
    const cache = useMemo(() => new Map(), [referenceTime]);
    return useCallback((t) => {
        if (!cache.has(t)) {
            cache.set(t, formatRelativeTime(t, referenceTime));
        }
        return cache.get(t);
    }, [referenceTime, cache]);
};

export const useSlug = (text) => useMemo(() => {
    let slug = text.replace(/[^a-zA-Z0-9\s]/g, "").replace(/ /g, "-").toLowerCase();
    slug = slug.slice(0, 32);
    if (slug.length === 0) {
        slug = 'x';
    }
    return slug;
}, [text]);

export function usePostLink(post) {
    const baseCommunity = useSelector(({baseCommunity}) => baseCommunity);
    const slug = useSlug(post?.title || "");
    if (!post) {
        return "";
    }
    if (baseCommunity === 'win' || baseCommunity !== post.community) {
        return `/c/${post.community}/p/${post.uuid}/${slug}/c`;
    }
    return `/p/${post.uuid}/${slug}/c`;
}

export const useIsBlocked = () => {
    const blocked = useSelector(({ blocked }) => blocked || []);
    return useCallback(
        (username) => typeof username === 'string' && binarySearch(blocked, username) !== -1,
        [blocked]
    );
};
